<template>
    <div class="items-quotation-import">
        <div class="import-section">
            <h5 class="mb-3">
                <i class="fa fa-upload"></i> Importar Items desde Excel
            </h5>
            
            <!-- Sección de subida de archivo -->
            <div v-if="!importBatch" class="upload-section">
                <div class="alert alert-info">
                    <i class="fa fa-info-circle"></i>
                    <strong>Formato requerido:</strong> Excel (.xlsx, .xls) o CSV
                    <br>
                    <small>
                        Columnas: nombre, referencia, valor unitario, categoria, descripcion (opcional), 
                        moneda (opcional), tipo unidad (opcional), mano de obra (opcional), imagen (opcional)
                    </small>
                </div>
                
                <b-form-file
                    v-model="file"
                    accept=".xlsx,.xls,.csv"
                    :state="fileState"
                    placeholder="Seleccione un archivo Excel..."
                    drop-placeholder="Arrastre el archivo aquí..."
                    @input="onFileSelected"
                ></b-form-file>
                
                <div class="mt-3">
                    <button 
                        class="btn btn-primary"
                        :disabled="!file || uploading"
                        @click="startImport"
                    >
                        <i class="fa fa-upload"></i> 
                        {{ uploading ? 'Subiendo...' : 'Iniciar Importación' }}
                    </button>
                </div>
            </div>

            <!-- Sección de progreso -->
            <div v-else class="progress-section">
                <div class="card">
                    <div class="card-header">
                        <h5 class="mb-0">
                            <i class="fa" :class="statusIcon"></i>
                            {{ statusText }}
                        </h5>
                    </div>
                    <div class="card-body">
                        <!-- Barra de progreso -->
                        <div v-if="importBatch.status === 'processing'" class="mb-3">
                            <div class="progress" style="height: 30px;">
                                <div 
                                    class="progress-bar progress-bar-striped progress-bar-animated"
                                    :style="{ width: progressPercentage + '%' }"
                                    role="progressbar"
                                >
                                    {{ progressPercentage.toFixed(1) }}%
                                </div>
                            </div>
                            <small class="text-muted">
                                Procesando filas... Por favor espere.
                            </small>
                        </div>

                        <!-- Estadísticas -->
                        <div class="import-stats">
                            <div class="row">
                                <div class="col-md-4">
                                    <div class="stat-card">
                                        <div class="stat-value">{{ importBatch.total_rows || 0 }}</div>
                                        <div class="stat-label">Total Filas</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="stat-card stat-success">
                                        <div class="stat-value">{{ importBatch.created_count || 0 }}</div>
                                        <div class="stat-label">Creados</div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="stat-card stat-error">
                                        <div class="stat-value">{{ importBatch.error_count || 0 }}</div>
                                        <div class="stat-label">Con Error</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Botones de acción -->
                        <div class="mt-3">
                            <button 
                                class="btn btn-secondary"
                                @click="resetImport"
                            >
                                <i class="fa fa-times"></i> Cerrar
                            </button>
                        </div>

                        <!-- Mensaje de error fatal o más de 20 errores (rollback) -->
                        <div v-if="importBatch.status === 'failed'" class="alert alert-danger mt-3">
                            <i class="fa fa-exclamation-triangle"></i>
                            <strong>Error:</strong> {{ importBatch.error_message }}
                        </div>

                        <!-- Mensaje de éxito sin errores -->
                        <div v-if="importBatch.status === 'finished' && importBatch.error_count === 0" class="alert alert-success mt-3">
                            <i class="fa fa-check-circle"></i>
                            <strong>¡Importación completada exitosamente!</strong>
                            Se crearon {{ importBatch.created_count }} items.
                        </div>

                        <!-- Mensaje de éxito con algunos errores (<= 20) -->
                        <div v-if="importBatch.status === 'finished' && importBatch.error_count > 0" class="alert alert-warning mt-3">
                            <i class="fa fa-check-circle"></i>
                            Se crearon {{ importBatch.created_count }} items. {{ importBatch.error_count }} fila(s) con error (ver tabla abajo).
                        </div>

                        <!-- Lista de errores en modal (cuando hay errores) -->
                        <div
                            v-if="(importBatch.status === 'finished' || importBatch.status === 'failed') && importBatch.error_count > 0 && importBatch.errors && importBatch.errors.length"
                            class="mt-3"
                        >
                            <h6 class="text-danger">
                                <i class="fa fa-list"></i> Errores por fila ({{ importBatch.errors.length }})
                            </h6>
                            <div class="table-responsive" style="max-height: 280px; overflow-y: auto;">
                                <table class="table table-sm table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Fila</th>
                                            <th>Referencia</th>
                                            <th>Campo</th>
                                            <th>Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(err, idx) in importBatch.errors" :key="idx">
                                            <td>{{ err.row_number }}</td>
                                            <td>{{ err.reference }}</td>
                                            <td>{{ err.field }}</td>
                                            <td>{{ err.message }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import itemsQuotationRepository from '../../../../repositories/items-quotation.repository';

export default {
    name: 'ItemsQuotationImport',
    data() {
        return {
            file: null,
            uploading: false,
            importBatch: null,
            statusCheckInterval: null,
        };
    },
    computed: {
        fileState() {
            if (!this.file) return null;
            const validTypes = ['.xlsx', '.xls', '.csv'];
            const fileName = this.file.name.toLowerCase();
            return validTypes.some(type => fileName.endsWith(type));
        },
        statusIcon() {
            if (!this.importBatch) return 'fa-upload';
            switch (this.importBatch.status) {
                case 'pending':
                    return 'fa-clock';
                case 'processing':
                    return 'fa-spinner fa-spin';
                case 'finished':
                    return 'fa-check-circle';
                case 'failed':
                    return 'fa-exclamation-triangle';
                default:
                    return 'fa-upload';
            }
        },
        statusText() {
            if (!this.importBatch) return 'Preparando importación...';
            switch (this.importBatch.status) {
                case 'pending':
                    return 'Importación pendiente';
                case 'processing':
                    return 'Importación en proceso';
                case 'finished':
                    return 'Importación completada';
                case 'failed':
                    return 'Importación fallida';
                default:
                    return 'Estado desconocido';
            }
        },
        progressPercentage() {
            if (!this.importBatch || !this.importBatch.total_rows || this.importBatch.total_rows === 0) {
                return 0;
            }
            const processed = (this.importBatch.created_count || 0) + (this.importBatch.error_count || 0);
            return (processed / this.importBatch.total_rows) * 100;
        }
    },
    methods: {
        onFileSelected(file) {
            if (file && this.fileState === false) {
                toastr.error('Formato de archivo no válido. Use .xlsx, .xls o .csv');
                this.file = null;
            }
        },
        startImport() {
            if (!this.file) {
                toastr.error('Por favor seleccione un archivo');
                return;
            }

            this.uploading = true;
            const formData = new FormData();
            formData.append('file', this.file);

            const pr = { ...itemsQuotationRepository };
            pr.importItems(formData)
                .then(response => {
                    this.uploading = false;
                    if (response && response.status) {
                        this.importBatch = {
                            id: response.batch_id,
                            file_name: this.file.name,
                            status: 'pending',
                            total_rows: 0,
                            created_count: 0,
                            error_count: 0,
                        };
                        toastr.success(response.msg || 'Importación iniciada correctamente');
                        this.startStatusCheck();
                    } else {
                        toastr.error(response?.msg || 'Error al iniciar la importación');
                    }
                })
                .catch(error => {
                    this.uploading = false;
                    console.error('Error al iniciar importación:', error);
                    toastr.error('Ocurrió un error al iniciar la importación');
                });
        },
        startStatusCheck() {
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval);
            }

            // Verificar estado cada 2 segundos
            this.statusCheckInterval = setInterval(() => {
                this.checkStatus();
            }, 2000);

            // Primera verificación inmediata
            this.checkStatus();
        },
        checkStatus() {
            if (!this.importBatch || !this.importBatch.id) {
                return;
            }

            const pr = { ...itemsQuotationRepository };
            pr.getImportStatus(this.importBatch.id)
                .then(response => {
                    if (response && response.status && response.batch) {
                        this.importBatch = response.batch;

                        // Si la importación terminó, detener el polling
                        if (this.importBatch.status === 'finished' || this.importBatch.status === 'failed') {
                            this.stopStatusCheck();
                            
                            if (this.importBatch.status === 'finished') {
                                // Emitir evento para recargar la lista
                                this.$emit('import-completed');
                            }
                        }
                    }
                })
                .catch(error => {
                    console.error('Error al verificar estado:', error);
                    // No mostrar error al usuario, solo loguear
                });
        },
        stopStatusCheck() {
            if (this.statusCheckInterval) {
                clearInterval(this.statusCheckInterval);
                this.statusCheckInterval = null;
            }
        },
        resetImport() {
            this.stopStatusCheck();
            this.importBatch = null;
            this.file = null;
            this.uploading = false;
        }
    },
    beforeDestroy() {
        this.stopStatusCheck();
    }
};
</script>

<style scoped lang="scss">
.items-quotation-import {
    .import-section {
        margin-bottom: 2rem;
    }

    .upload-section {
        .alert {
            margin-bottom: 1rem;
        }
    }

    .progress-section {
        .card {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;

            h5 {
                margin: 0;
            }
        }

        .progress {
            border-radius: 4px;
            overflow: hidden;
        }

        .import-stats {
            margin: 1.5rem 0;

            .stat-card {
                text-align: center;
                padding: 1rem;
                border-radius: 8px;
                background: #f8f9fa;
                border: 2px solid #e9ecef;
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                &.stat-success {
                    background: #d4edda;
                    border-color: #c3e6cb;
                }

                &.stat-error {
                    background: #f8d7da;
                    border-color: #f5c6cb;
                }

                .stat-value {
                    font-size: 2rem;
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 0.5rem;
                }

                .stat-label {
                    font-size: 0.9rem;
                    color: #666;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
            }
        }
    }
}
</style>

